import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { resolveSeo, setDocumentSeo } from "@/lib/seo";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ScrollToTop } from "@/components/ScrollToTop";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import { PromoBanner } from "@/components/PromoBanner";
import { GoogleTranslateHost } from "@/components/GoogleTranslateHost";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import KitFinder from "@/pages/KitFinder";
import ProductDetail from "@/pages/ProductDetail";
import BuildYourOwnBox from "@/pages/BuildYourOwnBox";
import DigitalDownloads from "@/pages/DigitalDownloads";
import OnlineJournal from "@/pages/OnlineJournal";
import About from "@/pages/About";
import OurStory from "@/pages/OurStory";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import BrandBible from "@/pages/BrandBible";
import Policies from "@/pages/Policies";
import TermsConditions from "@/pages/TermsConditions";import Subscriptions from "@/pages/Subscriptions";
import ProgramPage from "@/pages/ProgramPage";
import HopeCards from "@/pages/HopeCards";
import GraceInAction from "@/pages/GraceInAction";
import Welcome from "@/pages/Welcome";
import CartPage from "@/pages/CartPage";
import CheckoutReturn from "@/pages/CheckoutReturn";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RecipientUpload from "@/pages/RecipientUpload";
import Admin from "@/pages/Admin";

const Shell = ({ children }) => (
  <div className="App flex flex-col min-h-screen">
    <GoogleTranslateHost />
    <PromoBanner />
    <Navbar />
    <main className="flex-1">{children}</main>
    <Footer />
    <NewsletterPopup />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-screen flex items-center justify-center text-[#8A8175]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Sets a unique <title> + meta description per route (detail pages set their own).
const RouteSeo = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/product/") || pathname.startsWith("/blog/") || pathname.startsWith("/journal/") || pathname === "/admin") return;
    const seo = resolveSeo(pathname, search);
    if (seo) setDocumentSeo(seo.title, seo.description);
    else setDocumentSeo(null, null);
  }, [pathname, search]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <CurrencyProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RouteSeo />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Shell><Home /></Shell>} />
            <Route path="/shop" element={<Shell><Shop /></Shell>} />
            <Route path="/kit-finder" element={<Shell><KitFinder /></Shell>} />
            <Route path="/product/:slug" element={<Shell><ProductDetail /></Shell>} />
            <Route path="/build-your-own-box" element={<Shell><BuildYourOwnBox /></Shell>} />
            <Route path="/digital-downloads" element={<Shell><DigitalDownloads /></Shell>} />
            <Route path="/journal/:token" element={<OnlineJournal />} />
            <Route path="/about" element={<Shell><About /></Shell>} />
            <Route path="/our-story" element={<Shell><OurStory /></Shell>} />
            <Route path="/blog" element={<Shell><Blog /></Shell>} />
            <Route path="/blog/:slug" element={<Shell><BlogPost /></Shell>} />
            <Route path="/contact" element={<Shell><Contact /></Shell>} />
            <Route path="/brand-bible" element={<Shell><BrandBible /></Shell>} />
            <Route path="/policies" element={<Shell><Policies /></Shell>} />
            <Route path="/terms" element={<Shell><TermsConditions /></Shell>} />
            <Route path="/subscriptions" element={<Shell><Subscriptions /></Shell>} />
            <Route path="/care-boxes" element={<Shell><ProgramPage k="care-boxes" /></Shell>} />
            <Route path="/hope-cards" element={<Shell><HopeCards /></Shell>} />
            <Route path="/grace-in-action" element={<Shell><GraceInAction /></Shell>} />
            <Route path="/corporate-care" element={<Shell><ProgramPage k="corporate-care" /></Shell>} />
            <Route path="/church-ministry" element={<Shell><ProgramPage k="church-ministry" /></Shell>} />
            <Route path="/hospital-partnerships" element={<Shell><ProgramPage k="hospital-partnerships" /></Shell>} />
            <Route path="/monthly-club" element={<Shell><ProgramPage k="monthly-club" /></Shell>} />
            <Route path="/welcome" element={<Shell><Welcome /></Shell>} />
            <Route path="/cart" element={<Shell><CartPage /></Shell>} />
            <Route path="/checkout/return" element={<Shell><CheckoutReturn /></Shell>} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/programs/recipients/:token" element={<Shell><RecipientUpload /></Shell>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        </CurrencyProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
